import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {ServiceSubject} from '../../../../Models/ServiceSubject';
import {ServiceSubjectItem} from '../../../../Models/ServiceSubjectItem';
import {LanguageService} from '../../../../../translation/Services/language.service';
import {TranslationFormlyService} from '../../../../Services/translation.formly.service';
import {TranslateService} from '@ngx-translate/core';
import {ServiceSubjectItemService} from '../../../../Services/service-subject-item.service';
import {ServiceSubjectService} from '../../../../Services/service-subject.service';

@Component({
  selector: 'app-service-subject-item-overview',
  templateUrl: './service-subject-item-overview.component.html',
  styleUrls: ['./service-subject-item-overview.component.scss']
})
export class ServiceSubjectItemOverviewComponent implements OnInit {
  @Input() serviceSubject: ServiceSubject;
  public archive = false;
  public isEdit = false;
  public serviceSubjectItem = new ServiceSubjectItem();
  public serviceSubjectItemName: string;
  public serviceSubjectItemId: number;
  public languages = [];
  public languageMap = {};
  @ViewChild('deleteServiceRequestItemModal', {static: false}) deleteServiceRequestItemModal;
  @ViewChild('manageServiceRequestItemModal', {static: false}) manageServiceRequestItemModal;
  @ViewChild('manageServiceRequestItemForm', {static: false}) manageServiceRequestItemForm;
  @ViewChild('reAddServiceRequestItemModal', {static: false}) reAddServiceRequestItemModal;
  private serviceRequestItemTranslationFields = ['name', 'description'];
  public statuses = [
    {label: this.translateService.instant('global.active'), value: true},
    {label: this.translateService.instant('global.inactive'), value: false}
  ];
  public parents: any[] = [];

  constructor(
    private languageService: LanguageService,
    private serviceSubjectItemService: ServiceSubjectItemService,
    private translateService: TranslateService,
    private serviceSubjectService: ServiceSubjectService
  ) {
  }

  ngOnInit() {
    this.updateParents();
    this.languageService.list().subscribe(langResp => {
      this.languages = langResp.languages;
      this.languages.forEach(lang => {
        this.languageMap[lang.locale] = lang.locale;
      });
    });
  }

  positionChange(position: number, id: number) {
    this.serviceSubjectItemService.setPosition(position, id).subscribe(res => {
      if (res) {
        this.updateServiceSubject();
      }
    });
  }

  openReAddServiceSubjectItem(id: number, name: string) {
    this.serviceSubjectItemId = id;
    this.serviceSubjectItemName = name;
    this.reAddServiceRequestItemModal.show();
  }

  openSoftDeleteConfirm(id: number, name: string) {
    this.serviceSubjectItemId = id;
    this.serviceSubjectItemName = name;
    this.deleteServiceRequestItemModal.show();
  }

  openManageServiceSubjectItemModal(serviceSubjectItem: ServiceSubjectItem = null) {
    if (serviceSubjectItem) {
      this.isEdit = true;
      this.serviceSubjectItem = JSON.parse(JSON.stringify(serviceSubjectItem));
    } else {
      this.serviceSubjectItem = new ServiceSubjectItem();
      this.isEdit = false;
    }
    this.serviceSubjectItem.parent = this.serviceSubjectItem.parentId ? this.serviceSubjectItem.parentId : 0;
    this.serviceSubjectItem.translations = TranslationFormlyService.prepareTranslationFields(this.serviceRequestItemTranslationFields, this.serviceSubjectItem.translations, this.languages);
    this.serviceSubjectItem.translationKeys = TranslationFormlyService.findTranslationKeys(this.serviceSubjectItem);
    this.serviceSubjectItem.translationEntries = TranslationFormlyService.findTranslationEntries(this.serviceSubjectItem);
    this.updateParents();
    this.manageServiceRequestItemModal.show();
  }

  calculateIndent(idx: any) {
    let indent = ' - ';
    if (typeof idx === 'string') {
      const array = idx.split('.');
      for (let i = 1; i < array.length; i++) {
        indent += ' - ';
      }
    }
    return indent;
  }

  getServiceSubjectItems(): ServiceSubjectItem[] {
    const items = [];
    this.serviceSubject.serviceSubjectItems.forEach(item => {
      if (this.archive && item.deletedAt) {
        items.push(item);
      }
      if (!this.archive && !item.parentId && !item.deletedAt) {
        items.push(item);
      }
    });
    items.sort((a, b) => a.position > b.position ? 1 : -1);
    return items;
  }

  viewArchiveServiceRequestItems() {
    this.archive = true;
  }

  viewServiceRequestItems() {
    this.archive = false;
  }

  softDeleteServiceSubjectItem() {
    this.serviceSubjectItemService.deleteServiceSubjectItem(this.serviceSubjectItemId).subscribe(res => {
      this.deleteServiceRequestItemModal.hide();
      this.updateServiceSubject();
    });
  }

  reAddServiceRequestItem() {
    this.serviceSubjectItemService.reAddServiceSubjectItem(this.serviceSubjectItemId).subscribe(res => {
      this.reAddServiceRequestItemModal.hide();
      this.updateServiceSubject();
    });
  }

  submitManageServiceRequestItem() {
    const serviceSubjectItem = JSON.parse(JSON.stringify(this.serviceSubjectItem));
    serviceSubjectItem.translations = TranslationFormlyService.reorganizeTranslations(this.serviceRequestItemTranslationFields, this.serviceSubjectItem.translations);
    serviceSubjectItem.translations = serviceSubjectItem.translations.map(translation => {
      translation.content = this.serviceSubjectItem.translations[translation.field].nl;
      return translation;
    });
    if (serviceSubjectItem.parent === 0) {
      delete serviceSubjectItem.parent;
    }
    delete serviceSubjectItem.translationKeys;
    delete serviceSubjectItem.translationEntries;
    delete serviceSubjectItem.serviceSubjectItems;
    delete serviceSubjectItem.deletedAt;
    delete serviceSubjectItem.id;
    delete serviceSubjectItem.name;
    delete serviceSubjectItem.description;
    delete serviceSubjectItem.parentId;
    delete serviceSubjectItem.position;
    serviceSubjectItem.serviceSubject = this.serviceSubject.id;
    serviceSubjectItem.attachments = serviceSubjectItem.attachments ? serviceSubjectItem.attachments.map(attachment => attachment.id) : [];
    serviceSubjectItem.thumbnail = serviceSubjectItem.thumbnail ? serviceSubjectItem.thumbnail.id : null;
    this.serviceSubjectItemService.manageServiceSubjectItem(serviceSubjectItem, this.isEdit, this.serviceSubjectItem.id).subscribe(res => {
      this.updateServiceSubject();
      this.manageServiceRequestItemModal.hide();
      this.manageServiceRequestItemForm.reset();
    });
  }

  updateServiceSubject() {
    this.serviceSubjectService.getServiceSubject(this.serviceSubject.id).subscribe(serviceSubject => {
      this.serviceSubject = serviceSubject;
      this.updateParents();
    });
  }

  updateParents() {
    this.parents = [];
    this.parents.push({
      id: 0, name: this.serviceSubject.name
    });
    this.serviceSubject.serviceSubjectItems.forEach(item => {
      if (!item.deletedAt && this.serviceSubjectItem.id !== item.id ) {
        this.parents.push(item);
      }
    });
  }

  getFilteredItems(serviceSubjectItems: ServiceSubjectItem[]) {
    const items = [];
    serviceSubjectItems.forEach(item => {
      if (this.archive && item.deletedAt) {
        items.push(item);
      }
      if (!this.archive && !item.deletedAt) {
        items.push(item);
      }
    });
    items.sort((a, b) => a.position > b.position ? 1 : -1);
    return items;
  }
}
